import {KeyValuePipe} from '@angular/common'
import {Component, computed, inject} from '@angular/core'
import {MatCard} from '@angular/material/card'
import {MatIcon} from '@angular/material/icon'
import {LoanService} from '../../../services/loan.service'

interface ProgressStatus {
  isDone: boolean
  isActive: boolean
  name: string[]
  declined: boolean
}

/**
 * There are four levels of status
 */
enum ProgressStatusIndex {
  PENDING,
  REJECTED,
  ISSUABLE,
  APPROVED
}

interface RejectionReason {
  isOk: boolean
  name: string
}

type TRejectionReason =
  'uc'
  | 'income'
  | 'occupation'
  | 'kalp'
  | 'oldProperty'
  | 'credits'

@Component({
  selector: 'spb-kalp-status-progress',
  templateUrl: './kalp-status-progress.component.html',
  styleUrls: ['./kalp-status-progress.component.scss'],
  imports: [MatIcon, KeyValuePipe, MatCard]
})
export class KalpStatusProgressComponent {
  public readonly initialProgress: ProgressStatus[] = [
    {
      isDone: false,
      isActive: false,
      name: ['Ansökan', 'inskickad'],
      declined: false
    },
    {
      isDone: false,
      isActive: false,
      name: ['Kontroller', 'genomförda'],
      declined: false
    },
    {
      isDone: false,
      isActive: false,
      name: ['Lånelöfte', 'godkänt'],
      declined: false
    },
    {
      isDone: false,
      isActive: false,
      name: ['Lånelöfte', 'beviljat'],
      declined: false
    }
  ]

  public progress = computed(() => {
    const status = this.loanService.loanPromiseStatus()
    if (!status) return structuredClone(this.initialProgress)

    const newProgress = structuredClone(this.initialProgress)

    // Set isActive flag
    newProgress[ProgressStatusIndex.PENDING].isActive = false // "pending" status is never recovered from BE
    newProgress[ProgressStatusIndex.REJECTED].isActive = !status.canBeIssued && !status.complete
    newProgress[ProgressStatusIndex.ISSUABLE].isActive = status.canBeIssued
    newProgress[ProgressStatusIndex.APPROVED].isActive = status.complete
    newProgress[ProgressStatusIndex.REJECTED].declined = status.declined
    const activeIndex = newProgress.findIndex(s => s.isActive)

    // Set isDone flag
    newProgress.forEach((progressStatus, index) => {
      progressStatus.isDone = index < activeIndex
    })

    // Set special name for "manually issued loan", whenever the status is "manually_issued"
    if (newProgress[ProgressStatusIndex.APPROVED].isActive && status.completeManually) {
      newProgress[ProgressStatusIndex.APPROVED].name = ['Lånelöfte', 'beviljat', 'manuellt']
    }

    return newProgress
  })

  public rejectionReasons = computed(() => {
    const status = this.loanService.loanPromiseStatus()
    if (!status) return new Map<TRejectionReason, RejectionReason>()

    return new Map<TRejectionReason, RejectionReason>([
      ['uc', {
        name: 'UC',
        isOk: !status.permanentlyDenied && !status.needsUCAboveThreshold && !status.legacyManual
      }],
      ['kalp', {name: 'KALP', isOk: !status.needsBetterKalp}],
      ['income', {name: 'Inkomst', isOk: !status.needsIncomeCheck}],
      ['occupation', {
        name: 'Anställning',
        isOk: !status.needsOccupationCheck && !status.needsRetiredCheck
      }],
      ['credits', {
        name: 'Krediter',
        isOk: !status.needsPropertyLoansMatchingUC && !status.needsBlancoLoansThreshold && !status.needsBlancoLoansMatchingUC
      }],
      ['oldProperty', {name: 'Bostad', isOk: !status.needsPropertyCheck}]
    ])
  })

  public isManuallyIssued = computed(() => {
    const status = this.loanService.loanPromiseStatus()
    return status?.complete && status?.completeManually
  })

  protected readonly ProgressStatusIndex = ProgressStatusIndex

  private readonly loanService = inject(LoanService)

  public originalOrder(): number {
    return 0
  }
}
